import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useDeleteMessage, useEditMessage } from "../../api/queryHooks";
import { handleEditSend } from "../../api/EditedPrompt";
import useHeaderSelectedAIModel from "../../store/useHeaderSelectedAIModel";
import useStreamingStateStore from "../../store/useStreamingStateStore";
import useSessionIdStore from "../../store/useSessionIdStore";
import logger from "../../services/Logger";
import useFeedbackStore from "../../store/useFeedbackStore";
import { getFilesModelCompatibilityError } from "../../utils/fileModelCompatibility";
import PromptStore from "../../store/promptStore";

/**
 * Provides message manipulation handlers (delete, edit) with React Query mutations.
 * Handles optimistic updates, approval clearing, and streaming edit responses.
 * 
 * @param {string} chat_id - Current chat ID
 * @param {object} space - Spectra space object (or null for standalone chats)
 * @param {object} chat - Chat info object
 * @param {string} orgId - Organization ID
 * @param {object} user - User object with uid
 * @param {object} dependencies - Additional dependencies for edit functionality
 * @returns {object} Message handler functions
 */
export const useMessageHandlers = (
  chat_id,
  space,
  chat,
  orgId,
  user,
  {
    messages,
    setRegenerateIndex,
    setIsLatestMessageLoading,
    setAddChatDisabled,
    modeRef,
    apiUrl,
    generateStatus,
    setGenerateStatus,
    onApprovalCard,
    clearApprovals,
    queryClient,
    deviceId = null,
  }
) => {
  const deleteMutation = useDeleteMessage(chat_id, space?.space_id);
  const editMutation = useEditMessage(chat_id, space?.space_id);
  
  /**
   * Deletes a message with optimistic update
   */
  const deleteMessage = useCallback((idToDelete, answerId, deleteFollowing = false) => {
    if (!chat_id) {
      logger.warn('useMessageHandlers', 'Cannot delete message: missing chat_id', { orgId, userId: user?.uid });
      return;
    }

    const ownerId = space?.owner_id || user?.uid;
    
    deleteMutation.mutate({
      prompt_id: idToDelete,
      answer_id: answerId,
      chat_id: chat_id,
      space_id: space?.space_id,
      owner_id: ownerId,
      org_id: orgId,
      user_id: ownerId,
      by: user?.uid,
      session_id: useSessionIdStore.getState().session_id,
      delete_following: deleteFollowing,
    }, {
      onSuccess: () => {
        const streamingStore = useStreamingStateStore.getState();
        const promptIndex = messages.findIndex((message) => message.id === idToDelete);
        const removedMessages = deleteFollowing && promptIndex >= 0
          ? messages.slice(promptIndex)
          : messages.filter((message) => message.id === idToDelete);
        const removedMessageIds = new Set([idToDelete, answerId].filter(Boolean));
        removedMessages.forEach((message) => {
          removedMessageIds.add(message.id);
          (message.answers || []).forEach((answer) => removedMessageIds.add(answer.id));
        });
        removedMessageIds.forEach((messageId) => {
          streamingStore.clearMessageState(messageId);
        });
      },
    });
  }, [chat_id, space?.space_id, space?.owner_id, orgId, user?.uid, deleteMutation, messages]);
  
  /**
   * Edits a message and streams new response
   */
  const editFunctionality = useCallback((index, newPrompt, message_to_be_edited, styleSelection, styleVersionId, modelSelection = null) => {
    // Don't allow edits while a stream is already in progress
    if (generateStatus) return;

    const message_id = message_to_be_edited.id;
    const prompt_id = uuidv4();
    const answer_id = uuidv4();
    const turn_id = message_to_be_edited.turn_id || message_id;
    const source_prompt_id = message_to_be_edited.source_prompt_id || message_id;
    const source_answer_id = message_to_be_edited.source_answer_id;
    const prevPrompt = message_to_be_edited?.content;
    const assetIds = Array.isArray(message_to_be_edited?.asset_ids)
      ? message_to_be_edited.asset_ids.filter(
          (assetId) => typeof assetId === "string" && assetId.trim().length > 0
        )
      : [];
    const attachments = message_to_be_edited?.attachments || [];
    const selectedModel = modelSelection?.model || useHeaderSelectedAIModel.getState().selectedModel;
    const compatibilityError = getFilesModelCompatibilityError(attachments, selectedModel);
    if (compatibilityError) {
      useFeedbackStore.getState().showFeedback(compatibilityError, "error");
      return;
    }
    
    // Cancel any pending approvals for this message
    if (clearApprovals) {
      clearApprovals(message_id);
    }

    // Clear the previous streaming snapshot for this message immediately so the
    // old activity items don't remain visible while the new stream ramps up.
    useStreamingStateStore.getState().clearCompletedSnapshot(message_id);
    
    // Edit message with optimistic cache update
    const modelId = selectedModel?.id || null;
    
    editMutation.mutate({
      messageId: message_id,
      promptId: prompt_id,
      newPrompt,
      answerId: answer_id,
      modelId,
      userDetails: {
        name: user?.displayName || user?.name,
        email: user?.email,
        picture: user?.photoURL || user?.picture,
        user_id: user?.uid,
      },
    });
    PromptStore.getState().setSelectedAnswer(message_id, answer_id);

    handleEditSend({
      id: message_id,
      prompt_id,
      turn_id,
      source_prompt_id,
      source_answer_id,
      chat_id,
      messages,
      setRegenerateIndex,
      setIsLatestMessageLoading,
      setAddChatDisabled,
      user,
      orgId,
      assetIds,
      modeRef,
      apiUrl,
      index,
      newPrompt,
      prevPrompt,
      generateStatus,
      setGenerateStatus,
      space,
      answer_id,
      chat,
      onApprovalCard,
      queryClient,
      deviceId,
      styleSelection,
      styleVersionId,
      modelId,
      modelOverride: modelSelection?.isOverride ?? false,
      webSearchEnabled: modelSelection?.webSearchEnabled,
    });
  }, [
    chat_id,
    messages,
    setRegenerateIndex,
    setIsLatestMessageLoading,
    setAddChatDisabled,
    user,
    orgId,
    modeRef,
    apiUrl,
    generateStatus,
    setGenerateStatus,
    space,
    chat,
    onApprovalCard,
    clearApprovals,
    editMutation,
    queryClient,
    deviceId,
  ]);
  
  return {
    deleteMessage,
    editFunctionality,
  };
};
